import { useState, useEffect, useCallback } from 'react';
import {
  getMeNotificationsV1,
  updateMeNotificationsV1,
} from '../../api/me-—-notifications/me-—-notifications';
import {
  AppSettingsContentResponseModel,
  ConsentTypeContentResponseModel,
  NotificationPreferenceDto,
  NotificationTypeContentResponseModel,
  NotificationTypeContentResponseModelContentType,
  UpdateNotificationPreferencesRequest,
} from '../../api/model';
import { getContentById, getContents } from '../cms';
import { updateProfileConsents } from './consents';

type NotificationsResponse = { status: number; data: NotificationPreferenceDto[] };

export function useNotifications() {
  const [data, setData] = useState<NotificationsResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getMeNotificationsV1();
      setData(response as NotificationsResponse);
    } catch {
      // silent — caller checks data === undefined
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
}

export function useUpdateNotifications() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = useCallback(async (vars: { data: UpdateNotificationPreferencesRequest }) => {
    setIsPending(true);
    try {
      return await updateMeNotificationsV1(vars.data);
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync, isPending };
}

/**
 * Fetches all notification preference toggles for the authenticated user.
 *
 * Each record maps a CMS notification type node key to the user's opted-in/out state.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @returns Array of `NotificationPreferenceDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getProfileNotifications(): Promise<NotificationPreferenceDto[] | undefined> {
  try {
    const response = await getMeNotificationsV1();

    if (response.status === 200) {
      return response.data as NotificationPreferenceDto[];
    }

    console.warn(`getProfileNotifications: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getProfileNotifications: request failed:', error);
    return undefined;
  }
}

/**
 * Updates the enabled/disabled state for one or more notification types in a single batch.
 *
 * Sends a `PATCH /api/v1/me/notifications` with the provided preferences. Each entry
 * maps a CMS notification type node key to its new opted-in/out state. Existing preferences
 * not included in the request are left unchanged.
 * Authentication is injected automatically by the underlying `customFetch` interceptor.
 *
 * @param request - Batch of notification preference updates to apply.
 * @returns Array of updated `NotificationPreferenceDto` on HTTP 200.
 * @returns `undefined` on 400 (validation error), 401 (unauthenticated), or network error.
 */
export async function updateProfileNotifications(
  request: UpdateNotificationPreferencesRequest
): Promise<NotificationPreferenceDto[] | undefined> {
  try {
    const response = await updateMeNotificationsV1(request);

    if (response.status === 200) {
      return response.data as NotificationPreferenceDto[];
    }

    console.warn(`updateProfileNotifications: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('updateProfileNotifications: request failed:', error);
    return undefined;
  }
}

/**
 * Resolves a list of notification type items, hydrating any entries that are missing
 * properties data.
 */
function isStub(properties: Record<string, unknown> | null | undefined): boolean {
  return !properties || Object.keys(properties).length === 0;
}

export async function getNotificationTypes(
  items: NotificationTypeContentResponseModel[],
  locale?: string
): Promise<NotificationTypeContentResponseModel[]> {
  return Promise.all(
    items.map(async item => {
      const hydrated = isStub(item.properties as Record<string, unknown> | undefined)
        ? (((await getContentById(item.id, locale)) as NotificationTypeContentResponseModel | null) ?? item)
        : item;

      const deps = hydrated.properties?.consentsDependencies;
      if (!deps?.length) return hydrated;

      const hydratedDeps = await Promise.all(
        deps.map(async dep => {
          if (
            !isStub(
              (dep as ConsentTypeContentResponseModel).properties as Record<string, unknown> | undefined
            )
          ) {
            return dep;
          }
          const fetched = await getContentById(dep.id, locale);
          return (fetched as ConsentTypeContentResponseModel) ?? dep;
        })
      );

      return {
        ...hydrated,
        properties: {
          ...hydrated.properties,
          consentsDependencies: hydratedDeps,
        },
      } as NotificationTypeContentResponseModel;
    })
  );
}

export async function getAllNotificationTypes(
  locale?: string,
  appSettings?: AppSettingsContentResponseModel
): Promise<NotificationTypeContentResponseModel[]> {
  const repositoryId = appSettings?.properties?.notificationTypesRepository?.id ?? '';
  try {
    const result = await getContents(
      {
        fetch: `descendants:${repositoryId}`,
        filter: [`contentType:${NotificationTypeContentResponseModelContentType.notificationType}`],
        skip: 0,
        take: 999,
      },
      locale
    );

    return (result?.items ?? []) as NotificationTypeContentResponseModel[];
  } catch (error) {
    console.warn('getAllNotificationTypes: request failed:', error);
    return [];
  }
}

export async function enableAllNotificationCookies(
  locale?: string,
  appSettings?: AppSettingsContentResponseModel
): Promise<void> {
  const types = await getAllNotificationTypes(locale, appSettings);
  if (!types.length) return;

  const consentIds = [
    ...new Set(
      types
        .flatMap(type => (type.properties?.consentsDependencies ?? []) as ConsentTypeContentResponseModel[])
        .map(dep => dep.id)
        .filter((id): id is string => !!id)
    ),
  ];

  await Promise.all([
    updateProfileNotifications({
      notifications: types.map(type => ({
        notificationTypeNodeKey: type.id,
        isEnabled: true,
      })),
    }),
    consentIds.length
      ? updateProfileConsents({
          consents: consentIds.map(id => ({ consentTypeNodeKey: id, isOptedIn: true })),
        })
      : Promise.resolve(),
  ]);
}
